import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon, LayersIcon, BookIcon } from 'lucide-react';
import { COASegment } from './ChartOfAccounts';
interface GLCodeSelectorProps {
  onChange: (value: {
    type: 'full' | 'segment';
    code: string;
    segmentId?: string;
    segmentName?: string;
    level?: number;
  }) => void;
  value?: {
    type: 'full' | 'segment';
    code: string;
    segmentId?: string;
    segmentName?: string;
    level?: number;
  };
  coaData?: any[]; // Accept any structure for the chart of accounts data
}
export function GLCodeSelector({
  onChange,
  value,
  coaData = []
}: GLCodeSelectorProps) {
  const [selectorType, setSelectorType] = useState<'full' | 'segment'>(value?.type || 'full');
  const [selectedGLCode, setSelectedGLCode] = useState(value?.code || '');
  const [selectedSegment, setSelectedSegment] = useState(value?.segmentId || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Flatten COA data for easier selection
  const flattenedSegments = useMemo(() => {
    const result: any[] = [];
    const flatten = (codes: any[], parentPath = '', level = 1) => {
      codes.forEach(code => {
        const fullPath = parentPath ? `${parentPath}.${code.code}` : code.code;
        result.push({
          id: code.id,
          segmentCode: code.code,
          segmentName: code.name,
          fullGLPath: fullPath,
          level: level
        });
        if (code.children && code.children.length > 0) {
          flatten(code.children, fullPath, level + 1);
        }
      });
    };
    flatten(coaData);
    return result;
  }, [coaData]);
  // Get all unique segments
  const segments = useMemo(() => {
    const uniqueSegments = flattenedSegments.reduce((acc, segment) => {
      // Add segment if it's not already in the accumulator
      if (!acc.some(s => s.id === segment.id)) {
        acc.push(segment);
      }
      return acc;
    }, [] as any[]);
    return uniqueSegments.sort((a, b) => a.level - b.level);
  }, [flattenedSegments]);
  useEffect(() => {
    if (selectorType === 'full') {
      onChange({
        type: 'full',
        code: selectedGLCode
      });
    } else {
      const segment = segments.find(s => s.id === selectedSegment);
      if (segment) {
        onChange({
          type: 'segment',
          code: segment.segmentCode,
          segmentId: segment.id,
          segmentName: segment.segmentName,
          level: segment.level
        });
      }
    }
  }, [selectorType, selectedGLCode, selectedSegment, onChange, segments]);
  const handleGLCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedGLCode(e.target.value);
  };
  const handleSegmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSegment(e.target.value);
  };
  const handleSelectorTypeChange = (type: 'full' | 'segment') => {
    setSelectorType(type);
  };
  const handleSelectGLCode = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  return <div className="space-y-3" data-id="element-8086">
      <div className="flex items-center space-x-4" data-id="element-8087">
        <div className="flex items-center" data-id="element-8088">
          <input id="full-gl-code" type="radio" checked={selectorType === 'full'} onChange={() => handleSelectorTypeChange('full')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-8089" />
          <label htmlFor="full-gl-code" className="ml-2 block text-sm text-gray-700" data-id="element-8090">
            Full GL Code
          </label>
        </div>
        <div className="flex items-center" data-id="element-8091">
          <input id="gl-segment" type="radio" checked={selectorType === 'segment'} onChange={() => handleSelectorTypeChange('segment')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-8092" />
          <label htmlFor="gl-segment" className="ml-2 block text-sm text-gray-700" data-id="element-8093">
            GL Segment
          </label>
        </div>
      </div>
      {selectorType === 'full' ? <div className="relative" data-id="element-8094">
          <input type="text" value={selectedGLCode} onChange={handleGLCodeChange} placeholder="Enter GL Code" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8095" />
          <button type="button" onClick={handleSelectGLCode} className="absolute inset-y-0 right-0 px-3 flex items-center" data-id="element-8096">
            <ChevronRightIcon className="h-4 w-4 text-gray-400" data-id="element-8097" />
          </button>
          {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" data-id="element-8098">
              {flattenedSegments.map(segment => <div key={segment.id} className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-50" onClick={() => {
          setSelectedGLCode(segment.fullGLPath);
          setIsDropdownOpen(false);
        }} data-id="element-8099">
                  <div className="flex items-center" data-id="element-8100">
                    <BookIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8101" />
                    <span className="font-medium" data-id="element-8102">{segment.fullGLPath}</span>
                    <span className="ml-2 truncate text-gray-500" data-id="element-8103">
                      {segment.segmentName}
                    </span>
                  </div>
                </div>)}
            </div>}
        </div> : <div className="flex flex-col space-y-2" data-id="element-8104">
          <select value={selectedSegment} onChange={handleSegmentChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-8105">
            <option value="" data-id="element-8106">Select GL Segment</option>
            {segments.map(segment => <option key={segment.id} value={segment.id} data-id="element-8107">
                {segment.segmentCode} - {segment.segmentName} (Level{' '}
                {segment.level})
              </option>)}
          </select>
          {selectedSegment && <div className="text-sm text-gray-500 italic flex items-center" data-id="element-8108">
              <LayersIcon className="h-4 w-4 mr-1" data-id="element-8109" />
              <span data-id="element-8110">
                {(() => {
            const segment = segments.find(s => s.id === selectedSegment);
            return segment ? `Selected segment: ${segment.segmentCode} - ${segment.segmentName} (Level ${segment.level})` : 'No segment selected';
          })()}
              </span>
            </div>}
        </div>}
    </div>;
}